import axios from 'axios';
import uuid from 'react-uuid';

const USER_PREFIX = '__u__';
const isThemeKey = key => typeof key === 'string' && key.startsWith('theme-');

let hydratedUserId = null;
let hydrationPromise = null;
let pendingPatch = {};
let pendingUserId = null;
let patchTimer = null;

const safeParseUser = raw => {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
};

export const getStorageUserId = () => {
  const user = safeParseUser(window.localStorage.getItem('auth_user'));
  return user?._id || null;
};

const scopedKey = (key, userId) => `${USER_PREFIX}${userId}::${key}`;
const getAuthToken = () => window.localStorage.getItem('auth_token');

const safeParseJSON = raw => {
  if (typeof raw !== 'string') return null;
  try {
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
};

const buildPasswordEntryId = () => uuid().replaceAll('-', '');

export const normalizePasswordEntry = entry => {
  if (!entry || typeof entry !== 'object') return null;
  const password = String(entry.password || '').trim();
  if (!password) return null;
  return {
    id:
      typeof entry.id === 'string' && entry.id.trim() !== ''
        ? entry.id
        : buildPasswordEntryId(),
    label: String(entry.label || password).trim() || password,
    password,
    hidden: entry.hidden === true,
  };
};

export const normalizePasswordEntries = entries => {
  if (!Array.isArray(entries)) return [];
  const byPassword = new Map();
  entries.forEach(entry => {
    const normalized = normalizePasswordEntry(entry);
    if (!normalized) return;
    const previous = byPassword.get(normalized.password);
    byPassword.set(normalized.password, {
      ...normalized,
      id: previous?.id || normalized.id,
      label: normalized.label || previous?.label || normalized.password,
      hidden: normalized.hidden,
    });
  });
  return Array.from(byPassword.values());
};

const mergePasswordLists = (serverList, localList) => {
  const safeServer = normalizePasswordEntries(serverList);
  const safeLocal = normalizePasswordEntries(localList);

  const byPassword = new Map();

  // Server first as base, then local overrides/completes to avoid losing recent local edits.
  safeServer.forEach(entry => {
    const key = entry.password;
    byPassword.set(key, {
      id: entry.id || buildPasswordEntryId(),
      label: entry.label || key,
      password: key,
      hidden: entry.hidden === true,
    });
  });

  safeLocal.forEach(entry => {
    const key = entry.password;
    const previous = byPassword.get(key) || {};
    byPassword.set(key, {
      id: previous.id || entry.id || buildPasswordEntryId(),
      label: entry.label || previous.label || key,
      password: key,
      hidden:
        typeof entry.hidden === 'boolean'
          ? entry.hidden
          : previous.hidden === true,
    });
  });

  return Array.from(byPassword.values());
};

const mergePatch = (base, patch) => {
  const next = { ...(base || {}) };
  Object.entries(patch || {}).forEach(([key, value]) => {
    if (key === 'customThemes') {
      next.customThemes = { ...(next.customThemes || {}), ...(value || {}) };
      return;
    }
    next[key] = value;
  });
  return next;
};

const buildPreferencesPatchFromStorage = (key, value) => {
  if (!key) return null;
  if (
    key === 'passwords' ||
    key === 'favorites' ||
    key === 'recentItemIds' ||
    key === 'homeColumns' ||
    key === 'pinnedItemIds' ||
    key === 'hiddenItemIds'
  ) {
    const parsed = safeParseJSON(value);
    if (parsed === null) return null;
    return { [key]: parsed };
  }
  if (key === 'currentTheme') {
    const currentTheme = typeof value === 'string' ? value : '';
    const patch = { currentTheme };
    if (currentTheme && isThemeKey(currentTheme)) {
      const themeRaw = window.localStorage.getItem(currentTheme);
      const themeParsed = safeParseJSON(themeRaw);
      if (themeParsed && typeof themeParsed === 'object') {
        patch.customThemes = { [currentTheme]: themeParsed };
      }
    }
    return patch;
  }
  if (isThemeKey(key)) {
    const parsed = safeParseJSON(value);
    if (parsed && typeof parsed === 'object') {
      return { customThemes: { [key]: parsed } };
    }
  }
  return null;
};

const queuePreferencesPatch = patch => {
  const userId = getStorageUserId();
  const token = getAuthToken();
  if (!userId || !token || !patch || typeof patch !== 'object') {
    return;
  }
  if (pendingUserId && pendingUserId !== userId) {
    pendingPatch = {};
    if (patchTimer) {
      clearTimeout(patchTimer);
      patchTimer = null;
    }
  }
  pendingUserId = userId;
  pendingPatch = mergePatch(pendingPatch, patch);
  if (patchTimer) {
    clearTimeout(patchTimer);
  }
  patchTimer = setTimeout(async () => {
    const payload = pendingPatch;
    pendingPatch = {};
    patchTimer = null;
    try {
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/auth/preferences`,
        { preferences: payload },
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch (e) {
      // Keep local values as source of truth if API is unavailable.
    }
  }, 350);
};

export const getUserStorageKey = key => {
  const userId = getStorageUserId();
  if (!userId) return key;
  return scopedKey(key, userId);
};

export const getStorageValue = key => {
  const userId = getStorageUserId();
  if (!userId) {
    return window.localStorage.getItem(key);
  }
  const userKey = scopedKey(key, userId);
  const scopedValue = window.localStorage.getItem(userKey);
  if (scopedValue !== null) {
    return scopedValue;
  }
  return window.localStorage.getItem(key);
};

export const setStorageValue = (key, value, options = {}) => {
  const { mirrorGlobal = false } = options;
  const userId = getStorageUserId();
  if (userId) {
    window.localStorage.setItem(scopedKey(key, userId), value);
    if (mirrorGlobal) {
      window.localStorage.setItem(key, value);
    }
    const patch = buildPreferencesPatchFromStorage(key, value);
    if (patch) {
      queuePreferencesPatch(patch);
    }
    return;
  }
  window.localStorage.setItem(key, value);
};

export const removeStorageValue = key => {
  const userId = getStorageUserId();
  if (userId) {
    window.localStorage.removeItem(scopedKey(key, userId));
    return;
  }
  window.localStorage.removeItem(key);
};

export const getStorageJSON = (key, fallback = null) => {
  const raw = getStorageValue(key);
  if (raw === null) {
    return fallback;
  }
  try {
    return JSON.parse(raw);
  } catch (e) {
    return fallback;
  }
};

export const setStorageJSON = (key, value, options = {}) => {
  if (key === 'passwords') {
    setStorageValue(key, JSON.stringify(normalizePasswordEntries(value)), options);
    return;
  }
  setStorageValue(key, JSON.stringify(value), options);
};

export const migrateStorageKeysToCurrentUser = keys => {
  const userId = getStorageUserId();
  if (!userId || !Array.isArray(keys) || keys.length === 0) {
    return;
  }
  keys.forEach(key => {
    const source = window.localStorage.getItem(key);
    if (source === null) return;
    const targetKey = scopedKey(key, userId);
    if (window.localStorage.getItem(targetKey) !== null) return;
    window.localStorage.setItem(targetKey, source);
  });
};

export const ensureUserStorageHydrated = async () => {
  const userId = getStorageUserId();
  const token = getAuthToken();
  if (!userId || !token) {
    return false;
  }
  if (hydratedUserId === userId) {
    return true;
  }
  if (hydrationPromise) {
    return hydrationPromise;
  }

  hydrationPromise = (async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/auth/preferences`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const preferences = res?.data?.preferences || {};
      if (Array.isArray(preferences.passwords)) {
        const scopedPasswordsKey = scopedKey('passwords', userId);
        const localScoped = safeParseJSON(
          window.localStorage.getItem(scopedPasswordsKey),
        );
        const localGlobal = safeParseJSON(
          window.localStorage.getItem('passwords'),
        );
        const mergedPasswords = mergePasswordLists(
          preferences.passwords,
          Array.isArray(localScoped) ? localScoped : localGlobal,
        );

        window.localStorage.setItem(
          scopedPasswordsKey,
          JSON.stringify(mergedPasswords),
        );

        // If merge had to restore local-only entries, sync back server-side.
        if (JSON.stringify(mergedPasswords) !== JSON.stringify(preferences.passwords)) {
          queuePreferencesPatch({ passwords: mergedPasswords });
        }
      }
      if (Array.isArray(preferences.favorites)) {
        window.localStorage.setItem(
          scopedKey('favorites', userId),
          JSON.stringify(preferences.favorites),
        );
      }
      if (Array.isArray(preferences.recentItemIds)) {
        window.localStorage.setItem(
          scopedKey('recentItemIds', userId),
          JSON.stringify(preferences.recentItemIds),
        );
      }
      if (Array.isArray(preferences.pinnedItemIds)) {
        window.localStorage.setItem(
          scopedKey('pinnedItemIds', userId),
          JSON.stringify(preferences.pinnedItemIds),
        );
      }
      if (Array.isArray(preferences.hiddenItemIds)) {
        window.localStorage.setItem(
          scopedKey('hiddenItemIds', userId),
          JSON.stringify(preferences.hiddenItemIds),
        );
      }
      if (Array.isArray(preferences.homeColumns)) {
        window.localStorage.setItem(
          scopedKey('homeColumns', userId),
          JSON.stringify(preferences.homeColumns),
        );
      }
      if (typeof preferences.currentTheme === 'string') {
        window.localStorage.setItem(
          scopedKey('currentTheme', userId),
          preferences.currentTheme,
        );
      }
      if (
        preferences.customThemes &&
        typeof preferences.customThemes === 'object' &&
        !Array.isArray(preferences.customThemes)
      ) {
        Object.entries(preferences.customThemes).forEach(([themeKey, themeValue]) => {
          if (!isThemeKey(themeKey)) return;
          try {
            const serialized = JSON.stringify(themeValue || {});
            window.localStorage.setItem(themeKey, serialized);
            window.localStorage.setItem(scopedKey(themeKey, userId), serialized);
          } catch (e) {
            // ignore invalid theme payload
          }
        });
      }

      hydratedUserId = userId;
      return true;
    } catch (e) {
      return false;
    } finally {
      hydrationPromise = null;
    }
  })();

  return hydrationPromise;
};
